import { isBefore, subDays } from 'date-fns'
import { useCallback, useEffect } from 'react'

import { useLocalStorageState } from './useStorage'

interface AtmosphereTransportCacheModel {
  currentTransport: AtmosphereTransport
  lastSuccessfulTransportUpdate: Date
}

type AtmosphereTransport = 'websocket' | 'long-polling'

const defaultAtmosphereTransport: AtmosphereTransportCacheModel = {
  currentTransport: 'websocket',
  lastSuccessfulTransportUpdate: new Date(),
}

// Hook used to cache and manage atmosphere transports.
// Last successful transport is saved and used to evict transport fallback for configured time (default: 1 day)
export function useAtmosphereTransportCache() {
  const [transportCache, setTransportCache] = useLocalStorageState<AtmosphereTransportCacheModel>(
    'atmosphere-transport',
    { defaultValue: defaultAtmosphereTransport }
  )

  const reset = useCallback(() => {
    window.localStorage.removeItem('atmosphere-transport')
  }, [])

  useEffect(() => {
    if (!transportCache || isBefore(new Date(transportCache.lastSuccessfulTransportUpdate), subDays(new Date(), 1))) {
      reset()
    }
  }, [reset, transportCache])

  const setCurrentTransport = useCallback(
    (transport: AtmosphereTransport) =>
      transport !== transportCache.currentTransport &&
      setTransportCache({
        currentTransport: transport,
        lastSuccessfulTransportUpdate: transportCache.lastSuccessfulTransportUpdate,
      }),
    [setTransportCache, transportCache.currentTransport, transportCache.lastSuccessfulTransportUpdate]
  )
  return {
    currentTransport: transportCache.currentTransport,
    setCurrentTransport,
    reset,
  }
}
